import React, { useEffect, useState } from "react";
import { X, Copy, Check, Trash2, Link as LinkIcon, AlertTriangle, Share2 } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { http, errMsg } from "@/lib/api";
import { toast } from "sonner";

const EXPIRY_OPTIONS = [
    { value: "1h", label: "1 hour" },
    { value: "24h", label: "24 hours" },
    { value: "7d", label: "7 days" },
    { value: "30d", label: "30 days" },
];

export default function ShareModal({ jobId, onClose }) {
    const [expiresIn, setExpiresIn] = useState("24h");
    const [note, setNote] = useState("");
    const [creating, setCreating] = useState(false);
    const [freshLink, setFreshLink] = useState(null);
    const [links, setLinks] = useState([]);
    const [err, setErr] = useState("");
    const [copied, setCopied] = useState(false);

    const loadLinks = async () => {
        try {
            const { data } = await http.get(`/jobs/${jobId}/shares`);
            setLinks(data);
        } catch (e) {
            setErr(errMsg(e));
        }
    };

    useEffect(() => {
        loadLinks();
    }, [jobId]);

    const create = async () => {
        setCreating(true);
        setErr("");
        try {
            const { data } = await http.post(`/jobs/${jobId}/share`, {
                expires_in: expiresIn,
                note: note || null,
            });
            const url = `${window.location.origin}/share/${data.token}`;
            setFreshLink({ ...data, url });
            setNote("");
            await loadLinks();
        } catch (e) {
            setErr(errMsg(e));
        } finally {
            setCreating(false);
        }
    };

    const copyLink = async () => {
        if (!freshLink?.url) return;
        try {
            await navigator.clipboard.writeText(freshLink.url);
            setCopied(true);
            toast.success("Link copied");
            setTimeout(() => setCopied(false), 1800);
        } catch {
            toast.error("Clipboard unavailable");
        }
    };

    const revoke = async (linkId) => {
        if (!window.confirm("Revoke this share link? Anyone with the URL will immediately lose access.")) return;
        try {
            await http.delete(`/share/${linkId}`);
            toast.success("Link revoked");
            await loadLinks();
        } catch (e) {
            toast.error(errMsg(e));
        }
    };

    const fmtDate = (iso) => {
        try {
            return new Date(iso).toLocaleString();
        } catch {
            return iso;
        }
    };

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
            onClick={onClose}
            data-testid="share-modal-overlay"
        >
            <div
                className="relative mx-4 flex max-h-[90vh] w-full max-w-xl flex-col rounded-xl border border-border bg-surface shadow-2xl"
                onClick={(e) => e.stopPropagation()}
                data-testid="share-modal"
            >
                <button
                    onClick={onClose}
                    className="absolute right-3 top-3 rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
                    aria-label="Close"
                    data-testid="share-modal-close"
                >
                    <X size={18} />
                </button>

                <div className="shrink-0 border-b border-border p-5">
                    <div className="flex items-center gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-[#5b5ef0] to-[#7c5cff] text-white">
                            <Share2 size={18} />
                        </div>
                        <div>
                            <h3 className="font-display text-lg font-bold">Share live migration</h3>
                            <p className="text-xs text-muted-foreground">
                                Generate a read-only link. No MoovBytes account required to view.
                            </p>
                        </div>
                    </div>
                </div>

                <div className="flex-1 space-y-5 overflow-y-auto p-5">
                    {err && (
                        <div className="rounded-md border border-[hsl(var(--destructive))] bg-[hsl(var(--destructive))]/5 p-3 text-xs text-[hsl(var(--destructive))]">
                            {err}
                        </div>
                    )}

                    {/* Fresh link display */}
                    {freshLink && (
                        <div
                            className="rounded-lg border border-[#5b5ef0]/40 bg-gradient-to-br from-[#5b5ef0]/5 to-[#7c5cff]/5 p-4"
                            data-testid="fresh-share-link"
                        >
                            <div className="mb-2 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-[#5b5ef0]">
                                <AlertTriangle size={12} /> Save this link — not shown again
                            </div>
                            <div className="flex items-center gap-2 rounded-md border border-border bg-background p-2">
                                <LinkIcon size={14} className="shrink-0 text-muted-foreground" />
                                <code
                                    className="flex-1 truncate font-mono text-xs"
                                    data-testid="fresh-share-url"
                                    title={freshLink.url}
                                >
                                    {freshLink.url}
                                </code>
                                <button
                                    onClick={copyLink}
                                    className="flex items-center gap-1 rounded-md bg-[#5b5ef0] px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-[#4e51e0]"
                                    data-testid="copy-share-link-btn"
                                >
                                    {copied ? <Check size={13} /> : <Copy size={13} />}
                                    {copied ? "Copied" : "Copy"}
                                </button>
                            </div>
                            <div className="mt-3 flex items-start gap-3">
                                <div
                                    className="shrink-0 rounded-md border border-border bg-white p-2"
                                    data-testid="share-qr"
                                >
                                    <QRCodeSVG
                                        value={freshLink.url}
                                        size={96}
                                        level="M"
                                        includeMargin={false}
                                    />
                                </div>
                                <div className="flex-1 text-[11px] leading-relaxed text-muted-foreground">
                                    <div className="mb-1 font-semibold text-foreground">
                                        Scan from mobile
                                    </div>
                                    Point your phone camera at the code to open the read-only
                                    dashboard instantly — ideal for on-call engineers watching a
                                    migration away from their laptop.
                                </div>
                            </div>
                            <div className="mt-2 text-[11px] text-muted-foreground">
                                Expires {fmtDate(freshLink.expires_at)}
                            </div>
                        </div>
                    )}

                    {/* Create form */}
                    <div>
                        <div className="mb-2 font-mono text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
                            Create new link
                        </div>
                        <div className="space-y-3">
                            <div>
                                <label className="mb-1 block text-xs font-medium text-muted-foreground">
                                    Expires in
                                </label>
                                <div className="grid grid-cols-4 gap-2">
                                    {EXPIRY_OPTIONS.map((opt) => (
                                        <button
                                            key={opt.value}
                                            onClick={() => setExpiresIn(opt.value)}
                                            className={`rounded-md border px-3 py-2 text-xs font-medium transition-colors ${
                                                expiresIn === opt.value
                                                    ? "border-[#5b5ef0] bg-[#5b5ef0]/10 text-[#5b5ef0]"
                                                    : "border-border hover:border-foreground/30"
                                            }`}
                                            data-testid={`share-expiry-${opt.value}`}
                                        >
                                            {opt.label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <div>
                                <label className="mb-1 block text-xs font-medium text-muted-foreground">
                                    Note (optional)
                                </label>
                                <input
                                    type="text"
                                    value={note}
                                    onChange={(e) => setNote(e.target.value)}
                                    placeholder="e.g. Acme Corp stakeholders"
                                    maxLength={200}
                                    className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:border-[#5b5ef0] focus:outline-none"
                                    data-testid="share-note-input"
                                />
                            </div>
                            <button
                                onClick={create}
                                disabled={creating}
                                className="w-full rounded-md bg-gradient-to-r from-[#5b5ef0] to-[#7c5cff] py-2.5 text-sm font-semibold text-white shadow-lg shadow-[#5b5ef0]/25 transition-all hover:shadow-xl hover:shadow-[#5b5ef0]/40 disabled:opacity-60"
                                data-testid="create-share-link-btn"
                            >
                                {creating ? "Creating…" : "Generate share link"}
                            </button>
                        </div>
                    </div>

                    {/* Existing links */}
                    <div>
                        <div className="mb-2 font-mono text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
                            Active links ({links.filter((l) => l.active).length})
                        </div>
                        {links.length === 0 ? (
                            <div className="rounded-md border border-dashed border-border py-6 text-center text-xs text-muted-foreground">
                                No links created yet.
                            </div>
                        ) : (
                            <div className="space-y-2" data-testid="share-links-list">
                                {links.map((l) => (
                                    <div
                                        key={l.id}
                                        className="flex items-center justify-between gap-2 rounded-md border border-border bg-background p-2.5 text-xs"
                                        data-testid={`share-link-row-${l.id}`}
                                    >
                                        <div className="min-w-0">
                                            <div className="flex items-center gap-2">
                                                <span
                                                    className={`rounded-full px-2 py-0.5 text-[9px] font-semibold uppercase tracking-wider ${
                                                        l.active
                                                            ? "bg-[hsl(var(--success))]/15 text-[hsl(var(--success))]"
                                                            : "bg-muted text-muted-foreground"
                                                    }`}
                                                >
                                                    {l.active ? "Active" : l.revoked ? "Revoked" : "Expired"}
                                                </span>
                                                <span className="font-medium">{l.expires_in}</span>
                                                {l.note && (
                                                    <span className="truncate text-muted-foreground">
                                                        · {l.note}
                                                    </span>
                                                )}
                                            </div>
                                            <div className="mt-0.5 truncate text-[10px] text-muted-foreground">
                                                by {l.created_by} · {fmtDate(l.created_at)} · {l.view_count || 0} views
                                            </div>
                                        </div>
                                        {l.active && (
                                            <button
                                                onClick={() => revoke(l.id)}
                                                className="flex shrink-0 items-center gap-1 rounded-md border border-border px-2 py-1 text-[10px] font-semibold text-muted-foreground transition-colors hover:border-[hsl(var(--destructive))] hover:text-[hsl(var(--destructive))]"
                                                data-testid={`revoke-share-link-${l.id}`}
                                            >
                                                <Trash2 size={11} /> Revoke
                                            </button>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
